import { Component } from '@angular/core';
import { BaseViewComponent, Environment, FormBase, Optional, PaymentContext, PaymentContextScope, PaymentProcessingScope, PaymentSourceMode, boolean } from '@sb/library/base';
import { PaymentProcessorType, PaymentSourceIntent, PaymentSourceRegistrationNextAction, PaymentSourceType, ProcessPaymentSourceResultItem, SignupResultItem, StripePaymentSourceInputItem, SurchargeResultItem, SurchargeType } from '@sb/library/data-models';
import { Stripe, StripeElements, StripeError } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { PaymentErrorDialogComponent, PaymentErrorDialogScope } from '../../dialogs/payment-error-dialog.component';

class StripeAddPaymentSourceForm extends FormBase {
    AutoPay = Optional(boolean);
    SavePaymentSource = Optional(boolean);
}

@Component({
    selector: 'lib-payment-sources-add-stripe',
    templateUrl: './payment-sources-add-stripe.component.html',
    styleUrls: ['./payment-sources-add-stripe.component.scss']
})
export class PaymentSourcesAddStripeComponent extends BaseViewComponent implements PaymentErrorDialogScope {

    Scope = this.SetScope({
        Implements: PaymentErrorDialogScope,
        Requires: [PaymentContextScope, PaymentProcessingScope]
    });

    readonly form = StripeAddPaymentSourceForm.Create(false);

    private _totalAmountToPay: number | null = null;
    private _onlineConvenienceFee: number | null = null;

    private _paymentSourceType: PaymentSourceType = PaymentSourceType.CreditCard;
    private _loading: boolean = true;
    private _savePaymentMethodForFuture: boolean = false;
    private _stripeElements!: StripeElements;
    private _stripe: Stripe | null = null;
    private _stripeOptions!: any;

    constructor(
        private _Environment: Environment
    ) {
        super();
    }

    override ngOnInit(): void {
        super.ngOnInit();

        if (this.Scope.paymentProcessor!.Type != PaymentProcessorType.Stripe) {
            throw Error('Wrong payment processor type for payment-sources-add-stripe.component.ts')
        }

        if (this.Scope.schoolInfo.ForceAutoPay) {
            this.form.controls.AutoPay.setValue(true);
            this.form.controls.AutoPay.Disable();
        }

        this.ConnectTrigger(this.Scope.triggerUpdateAmountWithSurcharge, this.UpdateAmountWithSurcharge);
        this.ConnectTrigger(this.Scope.triggerProcessPaymentSource, this.ProcessPaymentSource);
        this.ConnectTrigger(this.Scope.triggerProcessPaymentSourceNextStep, this.ProcessPaymentSourceNextStep);
        this.ConnectTrigger(this.Scope.triggerReenablePaymentProcessorForm, () => this.form.Enable);

        if (!this._stripe) {
            loadStripe(this._Environment!.Payment!.Stripe!.PublicKey!, {
                stripeAccount: this.Scope.paymentProcessor!.AccountID!
            }).then(stripe => {
                if (!stripe) {
                    throw Error('The payment processor could not be loaded');
                }
                this._stripe = stripe;
                this.RenderStripeElements();
                this.SetDefaultStateForSavePaymentMethodForFuture();
            });
        } else {
            this.RenderStripeElements();
            this.SetDefaultStateForSavePaymentMethodForFuture();
        }
    }

    SavePaymentMethodForFuture(savePaymentMethod: boolean): void {
        this._savePaymentMethodForFuture = savePaymentMethod;
        if (!savePaymentMethod && !this.Scope.schoolInfo.ForceAutoPay) {
            this.form.controls.AutoPay.setValue(false);
        }
        this._stripeOptions.setupFutureUsage = savePaymentMethod ? 'off_session' : null;
        this._stripeElements.update(this._stripeOptions);
    }

    PaymentErrorDismissed() {
        this.form.Enable();
        this.Scope.PaymentProcessorReady();
    }

    private ProcessPaymentSource() {
        this._stripeElements!.submit().then(value => {
            if (value.error) {
                this.GetStripeError(value.error);
            } else {
                this._stripe!.createPaymentMethod({
                    elements: this._stripeElements
                }).then(result => {
                    if (result.error) {
                        this.GetStripeError(result.error)
                    } else {
                        const inputItem = new StripePaymentSourceInputItem({
                            ParentID: this.Scope.paymentDetails.ParentID,
                            FamilyID: this.Scope.paymentDetails.FamilyID,
                            PaymentProcessorID: this.Scope.paymentProcessor!.ID,
                            AutoPay: this.form.controls.AutoPay.value,
                            OrderIndex: 0,
                            PaymentMethodID: result.paymentMethod.id!,
                            ReturnUrl: window.location.href
                        });
                        switch (this.Scope.mode) {
                            case PaymentSourceMode.Add:
                                inputItem.Intent = PaymentSourceIntent.Vault;
                                break;
                            case PaymentSourceMode.Payment:
                            case PaymentSourceMode.AnonymousPayment:
                                inputItem.Intent = (this.form.controls.SavePaymentSource.value) ? PaymentSourceIntent.PayAndVault : PaymentSourceIntent.Pay;
                                inputItem.Surcharge = this._onlineConvenienceFee ?? 0;
                                inputItem.Total = this._totalAmountToPay;
                                break;
                        }
                        if (inputItem.Intent === PaymentSourceIntent.Pay) {
                            inputItem.AutoPay = null;
                            inputItem.OrderIndex = null;
                        }

                        if (this.Scope.mode === PaymentSourceMode.Add && this.paymentSourceType === PaymentSourceType.iDEAL) {
                            // iDEAL don't support SetupIntents, so vaulting them requires charging and refunding a minimal amount instead.
                            inputItem.Intent = PaymentSourceIntent.VaultByRefund;
                            inputItem.Surcharge = 0;
                            inputItem.Total = this.minimumPaymentWithFudgeFactor;
                        }

                        this.Scope.ProcessPaymentSourceComplete(inputItem);
                    }
                });
            }
        });
    }

    private ProcessPaymentSourceNextStep(result: ProcessPaymentSourceResultItem | SignupResultItem) {
        switch (result.NextAction) {
            case PaymentSourceRegistrationNextAction.UseStripeSdk:
                this._stripe?.handleNextAction({ clientSecret: result.StripeClientSecret! }).then(value => {
                    if (value.error) {
                        this.GetStripeError(value.error)
                    } else if (value.paymentIntent) {
                        this.Scope.PaymentSourceNextStepComplete(value.paymentIntent.id);
                    } else if (value.setupIntent) {
                        this.Scope.PaymentSourceNextStepComplete(value.setupIntent.id);
                    } else {
                        throw Error('Stripe did not return an expected result');
                    }
                });
                break;
            case PaymentSourceRegistrationNextAction.RedirectToUrl:
                const paymentResult = result as ProcessPaymentSourceResultItem;
                if (paymentResult.StripePaymentMethodType === "ideal") {
                    this._stripe?.confirmIdealPayment(result.StripeClientSecret!, { return_url: paymentResult.ReturnURL ?? window.location.href });
                } else if (paymentResult.StripePaymentMethodType === "klarna") {
                    const returnUrl = paymentResult.ReturnURL ?? window.location.href;
                    if (this.Scope.mode === PaymentSourceMode.Add) {
                        this._stripe?.confirmSetup({
                            clientSecret: result.StripeClientSecret!,
                            confirmParams: { return_url: returnUrl },
                            redirect: 'always'
                        }).then(value => {
                            if (value.error) {
                                this.GetStripeError(value.error);
                            }
                        });
                    } else {
                        this._stripe?.confirmKlarnaPayment(result.StripeClientSecret!, { return_url: returnUrl }).then(value => {
                            if (value.error) {
                                this.GetStripeError(value.error);
                            }
                        });
                    }
                } else {
                    throw Error('Non-iDEAL/Klarna redirects unsupported');
                }
                break;
            default:
                throw Error(`Unsupported Stripe action ${result.NextAction}`);
        }
    }

    private SetDefaultStateForSavePaymentMethodForFuture(): void {
        const savePaymentForFutureIsDefaultOn = this.Scope.context === PaymentContext.SignupWidget && this.Scope.mode === PaymentSourceMode.Payment;
        this._savePaymentMethodForFuture = savePaymentForFutureIsDefaultOn;
        this.form.controls.SavePaymentSource.setValue(this._savePaymentMethodForFuture);
        this.SavePaymentMethodForFuture(this._savePaymentMethodForFuture);
    }

    private TotalAmountForStripe() {
        const totalBeforeMultiplier = this._totalAmountToPay ? this._totalAmountToPay : this.minimumPaymentWithFudgeFactor;
        return Math.round(totalBeforeMultiplier * this.Scope.paymentProcessor!.CurrencyMultiplier);
    }

    private UpdateAmountWithSurcharge(amountWithSurcharge: SurchargeResultItem): void {
        this._totalAmountToPay = amountWithSurcharge.Total;
        this._onlineConvenienceFee = amountWithSurcharge.Surcharge;

        if (this._stripeOptions && this.Scope.mode !== PaymentSourceMode.Add) {
            this._stripeOptions.amount = this.TotalAmountForStripe();
            this._stripeElements.update(this._stripeOptions);
        }
    }
    
    private RenderStripeElements() {
        const isPaymentFromSignupForm = this.Scope.context === PaymentContext.SignupWidget && this.Scope.mode === PaymentSourceMode.Payment;
        const paymentMethodConfiguration = this.Scope.context === PaymentContext.TeacherPortal ? this._Environment!.Payment!.Stripe!.TeacherPaymentMethodConfigurationKey
                                            : this._Environment!.Payment!.Stripe!.DefaultPaymentMethodConfigurationKey;
        const fromSignUpForm = this.Scope.context === PaymentContext.SignupWidget;

        this._stripeOptions = {
            mode: (this.Scope.mode === PaymentSourceMode.Add) ? 'setup' : 'payment',
            currency: this.Scope.schoolInfo.CurrencyCode.toLowerCase(),
            setupFutureUsage: null,
            paymentMethodCreation: 'manual',
            captureMethod: isPaymentFromSignupForm ? 'manual' : undefined,
            // only paymentMethodConfiguration or paymentMethodType can be set
            paymentMethodConfiguration: fromSignUpForm ? undefined : paymentMethodConfiguration,
            // force sign up form to only use card and link
            paymentMethodTypes: fromSignUpForm ? ['card', 'link'] : undefined,
            paymentMethodOptions: {}
        };

        if (this.Scope.mode === PaymentSourceMode.Payment || this.Scope.mode === PaymentSourceMode.AnonymousPayment) {
            this._stripeOptions.paymentMethodOptions.us_bank_account = { verification_method: 'instant' };
        }

        if (this.Scope.mode !== PaymentSourceMode.Add) {
            this._stripeOptions.amount = this.TotalAmountForStripe();
        }

        this._stripeElements = this._stripe!.elements(this._stripeOptions);
        const setupElement = this._stripeElements.create('payment');

        setupElement.on('ready', _ => {
            this._loading = false;
            this.Scope.PaymentProcessorReady();
        });

        setupElement.on('change', result => {
            this.form.controls.SavePaymentSource.Enable();
            switch (result.value.type) {
                case 'ideal':
                    this._paymentSourceType = PaymentSourceType.iDEAL;
                    break;
                case 'us_bank_account':
                case 'bacs_debit':
                case 'au_becs_debit':
                case 'sepa_debit':
                    this._paymentSourceType = PaymentSourceType.BankAccount;
                    break;
                case 'link':
                    this._paymentSourceType = PaymentSourceType.StripeLink;
                    break;
                case 'wechat_pay':
                    this.form.controls.SavePaymentSource.setValue(false);
                    this.form.controls.SavePaymentSource.Disable();
                    this._paymentSourceType = PaymentSourceType.WechatPay;
                    break;
                case 'klarna':
                    this._paymentSourceType = PaymentSourceType.Klarna;
                    break;
                case 'alipay':
                    this.form.controls.SavePaymentSource.setValue(false);
                    this.form.controls.SavePaymentSource.Disable();
                    this._paymentSourceType = PaymentSourceType.Alipay;
                    break;
                
                default:
                    this._paymentSourceType = PaymentSourceType.CreditCard;
            }

            this.Scope.PaymentSourceTypeChanged?.(this._paymentSourceType);
        });

        setupElement.on('loaderror', result => {
            if (result.error) {
                throw Error('Stripe could not load');
            }
        });
        setupElement.mount('#payment-element');
    }

    private GetStripeError(result: StripeError): void {
        switch (result.type) {
            case 'validation_error':
                this.PaymentErrorDismissed();
                break;
            default:
                this.CreateDialog(PaymentErrorDialogComponent, { IsProcessorError: true, ErrorMessage: result.message! });
                break;
        }
    }

    get loading(): boolean {
        return this._loading;
    }

    get bankSurcharge() {
        return this.Scope.schoolInfo.PaymentSurchargeACH;
    }

    get cardSurcharge() {
        return this.Scope.schoolInfo.PaymentSurchargeCreditCard;
    }

    get paymentSourceType(): PaymentSourceType {
        return this._paymentSourceType;
    }

    get PaymentSourceType(): typeof PaymentSourceType {
        return PaymentSourceType;
    }

    get SurchargeType(): typeof SurchargeType {
        return SurchargeType;
    }

    get PaymentSourceMode(): typeof PaymentSourceMode {
        return PaymentSourceMode;
    }

    get PaymentContext(): typeof PaymentContext {
        return PaymentContext;
    }

    get minimumPaymentWithFudgeFactor(): number {
        return this.Scope.paymentProcessor!.MinimumCharge * 3;
    }
}
